import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import FeatureGateNotice from '@/hub-shared/components/FeatureGateNotice';
import { __ } from '@/hub-shared/utils/i18n';
import type { PortalBootstrapData } from '../types/window';
import { fetchWithNonce } from '../utils/fetchWithNonce';
import { parseApiError } from '../utils/parseApiError';
import { errorToUserFacingMessage } from '../api/apiErrors';
import {
  NOTIFICATION_EVENT_LABELS,
  NOTIFICATION_GROUP_LABELS,
  NOTIFICATION_PARENT_LABELS,
} from './notificationPreferenceLabels';
import type {
  NotificationPreferenceDraft,
  NotificationPreferenceEventEntry,
  NotificationPreferenceParentControl,
  NotificationPreferencesPayload,
} from './notificationPreferenceTypes';

type NotificationsTabProps = {
  bootstrap: PortalBootstrapData;
  onSessionExpired: () => void;
};

type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'forbidden';
type SaveState = 'idle' | 'saving' | 'error';

const buildDraftFromPayload = (payload: NotificationPreferencesPayload): NotificationPreferenceDraft => {
  const draft: NotificationPreferenceDraft = {};

  for (const parent of payload.parent_controls) {
    draft[parent.parent_key] = {
      inbox: parent.inbox.effective,
      email: parent.email.effective,
    };
  }

  for (const group of payload.groups) {
    for (const event of group.events) {
      draft[event.event_code] = {
        inbox: event.inbox.effective,
        email: event.email.effective,
      };
    }
  }

  return draft;
};

const buildOverridesFromDraft = (
  payload: NotificationPreferencesPayload,
  draft: NotificationPreferenceDraft,
  baseline: NotificationPreferenceDraft,
): Array<{ event_code: string; channel: 'inbox' | 'email'; enabled: boolean }> => {
  const overrides: Array<{ event_code: string; channel: 'inbox' | 'email'; enabled: boolean }> = [];

  for (const parent of payload.parent_controls) {
    const current = draft[parent.parent_key];
    const original = baseline[parent.parent_key];
    if (current === undefined || original === undefined) {
      continue;
    }

    if (current.inbox !== original.inbox) {
      overrides.push({ event_code: parent.parent_key, channel: 'inbox', enabled: current.inbox });
    }

    if (parent.email.editable && current.email !== original.email) {
      overrides.push({ event_code: parent.parent_key, channel: 'email', enabled: current.email });
    }
  }

  for (const group of payload.groups) {
    for (const event of group.events) {
      if (event.parent_key !== undefined) {
        continue;
      }

      const current = draft[event.event_code];
      const original = baseline[event.event_code];
      if (current === undefined || original === undefined) {
        continue;
      }

      if (event.inbox.editable && current.inbox !== original.inbox) {
        overrides.push({ event_code: event.event_code, channel: 'inbox', enabled: current.inbox });
      }

      if (event.email.editable && current.email !== original.email) {
        overrides.push({ event_code: event.event_code, channel: 'email', enabled: current.email });
      }
    }
  }

  return overrides;
};

const isDraftDirty = (
  payload: NotificationPreferencesPayload,
  draft: NotificationPreferenceDraft,
  baseline: NotificationPreferenceDraft,
): boolean => buildOverridesFromDraft(payload, draft, baseline).length > 0;

const setParentChannel = (
  draft: NotificationPreferenceDraft,
  parent: NotificationPreferenceParentControl,
  channel: 'inbox' | 'email',
  enabled: boolean,
): NotificationPreferenceDraft => {
  const next = {
    ...draft,
    [parent.parent_key]: {
      inbox: draft[parent.parent_key]?.inbox ?? parent.inbox.effective,
      email: draft[parent.parent_key]?.email ?? parent.email.effective,
      [channel]: enabled,
    },
  };

  for (const childCode of parent.child_event_codes) {
    next[childCode] = {
      inbox: channel === 'inbox' ? enabled : (draft[childCode]?.inbox ?? next[childCode]?.inbox ?? enabled),
      email: channel === 'email' ? enabled : (draft[childCode]?.email ?? next[childCode]?.email ?? enabled),
    };
  }

  return next;
};

const NotificationsTab = ({ bootstrap, onSessionExpired }: NotificationsTabProps) => {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState('');
  const [payload, setPayload] = useState<NotificationPreferencesPayload | null>(null);
  const [draft, setDraft] = useState<NotificationPreferenceDraft>({});
  const [baselineDraft, setBaselineDraft] = useState<NotificationPreferenceDraft>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const [resetState, setResetState] = useState<'idle' | 'resetting'>('idle');

  const groupLabels = useMemo(() => NOTIFICATION_GROUP_LABELS(), []);
  const parentLabels = useMemo(() => NOTIFICATION_PARENT_LABELS(), []);
  const eventLabels = useMemo(() => NOTIFICATION_EVENT_LABELS(), []);

  const emailGateNotice = useMemo(() => {
    if (payload?.email_channel_globally_enabled !== false) {
      return null;
    }
    return {
      allowed: false,
      message: __('Les e-mails de notification sont désactivés sur cette plateforme. Vous pouvez toujours gérer les notifications dans l’application.'),
    };
  }, [payload?.email_channel_globally_enabled]);

  const loadPreferences = useCallback(async () => {
    setLoadState('loading');
    setLoadError('');

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}notifications/preferences`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (response.status === 403) {
        setLoadState('forbidden');
        return;
      }

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const json = (await response.json()) as { data?: NotificationPreferencesPayload };
      if (!json.data) {
        throw new Error(__('Réponse invalide du serveur.'));
      }

      const nextDraft = buildDraftFromPayload(json.data);
      setPayload(json.data);
      setDraft(nextDraft);
      setBaselineDraft(nextDraft);
      setLoadState('ready');
    } catch (e) {
      setLoadError(errorToUserFacingMessage(e, __('Impossible de charger les préférences.')));
      setLoadState('error');
    }
  }, [bootstrap.nonce, bootstrap.restUrls.accountingHub, onSessionExpired]);

  useEffect(() => {
    void loadPreferences();
  }, [loadPreferences]);

  const setChannel = (eventCode: string, channel: 'inbox' | 'email', enabled: boolean) => {
    setDraft((prev) => ({
      ...prev,
      [eventCode]: {
        inbox: prev[eventCode]?.inbox ?? true,
        email: prev[eventCode]?.email ?? true,
        [channel]: enabled,
      },
    }));
  };

  const handleSave = async () => {
    if (payload === null) {
      return;
    }

    const overrides = buildOverridesFromDraft(payload, draft, baselineDraft);
    if (overrides.length === 0) {
      return;
    }

    setSaveState('saving');
    setSaveError('');

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}notifications/preferences`, {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': bootstrap.nonce,
        },
        body: JSON.stringify({ overrides }),
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const json = (await response.json()) as { data?: NotificationPreferencesPayload };
      if (!json.data) {
        throw new Error(__('Réponse invalide du serveur.'));
      }

      const nextDraft = buildDraftFromPayload(json.data);
      setPayload(json.data);
      setDraft(nextDraft);
      setBaselineDraft(nextDraft);
      setSaveState('idle');
    } catch (e) {
      setSaveError(errorToUserFacingMessage(e, __('Enregistrement impossible.')));
      setSaveState('error');
    }
  };

  const handleReset = async () => {
    setResetState('resetting');
    setSaveError('');

    try {
      const response = await fetchWithNonce(`${bootstrap.restUrls.accountingHub}notifications/preferences/reset`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': bootstrap.nonce },
      });

      if (response.status === 401) {
        onSessionExpired();
        return;
      }

      if (!response.ok) {
        throw new Error(await parseApiError(response));
      }

      const json = (await response.json()) as { data?: NotificationPreferencesPayload };
      if (!json.data) {
        throw new Error(__('Réponse invalide du serveur.'));
      }

      const nextDraft = buildDraftFromPayload(json.data);
      setPayload(json.data);
      setDraft(nextDraft);
      setBaselineDraft(nextDraft);
      setSaveState('idle');
    } catch (e) {
      setSaveError(errorToUserFacingMessage(e, __('Réinitialisation impossible.')));
      setSaveState('error');
    } finally {
      setResetState('idle');
    }
  };

  const renderToggleRow = (
    key: string,
    label: string,
    description: string | undefined,
    inboxChecked: boolean,
    emailChecked: boolean,
    inboxDisabled: boolean,
    emailDisabled: boolean,
    onInboxChange: (enabled: boolean) => void,
    onEmailChange: (enabled: boolean) => void,
  ) => (
    <Fragment key={key}>
      <div className="notifications-prefs__row-label">
        <span className="notifications-prefs__event-name">{label}</span>
        {description ? <span className="notifications-prefs__event-desc">{description}</span> : null}
      </div>

      <div className="notifications-prefs__toggle-cell">
        <div className={`switch-container${inboxDisabled ? ' disabled' : ''}`}>
          <input
            id={`notif-inbox-${key}`}
            type="checkbox"
            className="switch"
            checked={inboxChecked}
            disabled={inboxDisabled}
            onChange={(e) => onInboxChange(e.target.checked)}
          />
          <label htmlFor={`notif-inbox-${key}`} className="screen-reader-text">
            {__('Notification application')}
          </label>
        </div>
      </div>

      <div className="notifications-prefs__toggle-cell">
        <div className={`switch-container${emailDisabled ? ' disabled' : ''}`}>
          <input
            id={`notif-email-${key}`}
            type="checkbox"
            className="switch"
            checked={emailChecked}
            disabled={emailDisabled}
            onChange={(e) => onEmailChange(e.target.checked)}
          />
          <label htmlFor={`notif-email-${key}`} className="screen-reader-text">
            {__('Notification e-mail')}
          </label>
        </div>
      </div>
    </Fragment>
  );

  const renderEventRow = (event: NotificationPreferenceEventEntry) => {
    const labels = eventLabels[event.event_code];
    const draftEntry = draft[event.event_code];
    const inboxDisabled = !event.inbox.editable;
    const emailDisabled = !event.email.editable;
    const rowDisabled = !event.configurable;

    return renderToggleRow(
      event.event_code,
      labels?.label ?? event.event_code,
      labels?.description,
      draftEntry?.inbox ?? event.inbox.effective,
      draftEntry?.email ?? event.email.effective,
      inboxDisabled || rowDisabled,
      emailDisabled || rowDisabled,
      (enabled) => setChannel(event.event_code, 'inbox', enabled),
      (enabled) => setChannel(event.event_code, 'email', enabled),
    );
  };

  if (loadState === 'forbidden') {
    return (
      <div className="container-style notifications-prefs">
        <FeatureGateNotice
          decision={{
            allowed: false,
            message: __('Les préférences de notification ne sont pas disponibles pour ce compte.'),
          }}
        />
      </div>
    );
  }

  if (loadState === 'loading' || loadState === 'idle') {
    return (
      <div className="container-style notifications-prefs">
        <p className="notifications-prefs__intro">{__('Chargement…')}</p>
      </div>
    );
  }

  if (loadState === 'error' || payload === null) {
    return (
      <div className="container-style notifications-prefs">
        <p className="accounting-hub-notice accounting-hub-notice-error">{loadError}</p>
      </div>
    );
  }

  const dirty = isDraftDirty(payload, draft, baselineDraft);
  const missionParent = payload.parent_controls.find(
    (parent) => parent.parent_key === 'mission.status_notifications',
  );

  return (
    <div className="container-style notifications-prefs">
      <p className="notifications-prefs__intro">
        {__('Choisissez les notifications reçues dans l’application et par e-mail pour le cabinet actif.')}
      </p>
      <p className="notifications-prefs__intro notifications-prefs__intro--secondary">
        {__(
          'Les notifications de mission distinguent la revue (diligences révisées à superviser) de la génération (diligences supervisées ou omises).',
        )}
      </p>
      {emailGateNotice ? <FeatureGateNotice decision={emailGateNotice} /> : null}

      <div className="notifications-prefs__table">
        <div className="notifications-prefs__col-head" aria-hidden="true" />
        <div className="notifications-prefs__col-head" aria-hidden="true">{__('Application')}</div>
        <div className="notifications-prefs__col-head" aria-hidden="true">{__('E-mail')}</div>

        {payload.groups.map((group) => (
          <Fragment key={group.group_key}>
            <h4 className="notifications-prefs__group-title">
              {groupLabels[group.group_key] ?? group.group_key}
            </h4>

            {group.events.map((event) => {
              if (event.parent_key !== undefined) {
                return null;
              }

              if (group.group_key === 'missions' && event.event_code === 'mission.assigned') {
                return renderEventRow(event);
              }

              return null;
            })}

            {group.group_key === 'missions' && missionParent
              ? renderToggleRow(
                  missionParent.parent_key,
                  parentLabels[missionParent.parent_key]?.label ?? missionParent.parent_key,
                  parentLabels[missionParent.parent_key]?.description,
                  draft[missionParent.parent_key]?.inbox ?? missionParent.inbox.effective,
                  draft[missionParent.parent_key]?.email ?? missionParent.email.effective,
                  !missionParent.inbox.editable,
                  !missionParent.email.editable,
                  (enabled) => {
                    setDraft((prev) => setParentChannel(prev, missionParent, 'inbox', enabled));
                  },
                  (enabled) => {
                    setDraft((prev) => setParentChannel(prev, missionParent, 'email', enabled));
                  },
                )
              : null}

            {group.events.map((event) => {
              if (event.event_code === 'mission.assigned') {
                return null;
              }

              return renderEventRow(event);
            })}
          </Fragment>
        ))}
      </div>

      <div className="role-permission-editor__actions">
        {saveState === 'error' ? (
          <span className="role-permission-editor__error">{saveError}</span>
        ) : null}
        <button
          type="button"
          className="btn-secondary"
          disabled={resetState === 'resetting' || saveState === 'saving'}
          onClick={() => { void handleReset(); }}
        >
          {resetState === 'resetting' ? __('Réinitialisation…') : __('Réinitialiser')}
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!dirty || saveState === 'saving' || resetState === 'resetting'}
          onClick={() => { void handleSave(); }}
        >
          {saveState === 'saving' ? __('Enregistrement…') : __('Enregistrer')}
        </button>
      </div>
    </div>
  );
};

export default NotificationsTab;
